// HR / Finance loans console, backed by credit-service (replaces the legacy
// payroll loan endpoints). Three sections: lending product config (enable a
// product + tune the rate within the platform ceiling + limits), the approval
// queue (approve/reject applications — the rate is set by the product config,
// so approval is one click), and an overview of active facilities.
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { DataState } from '@/features/shared/components/ui/DataState';
import toast from 'react-hot-toast';
import { SignaturePad, type SignaturePadHandle } from '@/features/shared/components/SignaturePad';
import { getApiErrorMessage, getSubmissionAdvice } from '@/lib/apiError';
import { Coins, Wallet, CircleDollarSign } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { useConfirm, usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { payrollApi } from '../api/payrollApi';
import { creditApi, applicationPill, facilityPill, type SettlementConfirmation } from '@/features/credit/api/creditApi';
import type {
  CreditApplication, CreditFacility, CreditProduct, CreditProgram,
} from '@/features/credit/types';
import type { EmployeeLite } from '../types';
import { formatMoney } from '../lib/format';
import { StatCard } from '../components/StatCard';

// Editable per-product config, seeded from the catalogue + the tenant's program.
interface ProductForm {
  enabled: boolean;
  rate: string; // annual rate override (blank = inherit product default)
  maxAmount: string; // 0/blank = product default
  maxTerm: string;
  workflow: string;
  saving: boolean;
}

export const LoansPage: React.FC = () => {
  const confirm = useConfirm();
  const [catalogue, setCatalogue] = useState<CreditProduct[]>([]);
  const [applications, setApplications] = useState<CreditApplication[]>([]);
  const [facilities, setFacilities] = useState<CreditFacility[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [forms, setForms] = useState<Record<string, ProductForm>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const input =
    'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100';

  const seedForms = (products: CreditProduct[], programs: CreditProgram[]) => {
    const byId = new Map(programs.map((p) => [p.productId, p]));
    const next: Record<string, ProductForm> = {};
    for (const p of products) {
      const prog = byId.get(p.id);
      next[p.id] = {
        enabled: prog?.enabled ?? false,
        rate: prog?.annualRateOverride != null ? String(prog.annualRateOverride) : '',
        maxAmount: prog?.maxAmountOverride != null ? String(prog.maxAmountOverride) : '',
        maxTerm: prog?.maxTermOverride != null ? String(prog.maxTermOverride) : '',
        workflow: prog?.approvalWorkflow ?? 'single',
        saving: false,
      };
    }
    setForms(next);
  };

  const load = () => {
    // US-17: the catalogue is the primary load — its failure is a page ERROR
    // (shown with Retry), not a silent "no products". Secondary loads degrade.
    setLoading(true); setLoadError(false);
    Promise.all([
      creditApi.listCatalogue(),
      creditApi.listPrograms().catch(() => []),
      creditApi.listApplications().catch(() => []),
      creditApi.listFacilities().catch(() => []),
      payrollApi.listEmployees().catch(() => [] as EmployeeLite[]),
    ])
      .then(([products, programs, apps, facs, emps]) => {
        setCatalogue(products);
        seedForms(products, programs);
        setApplications(apps);
        setFacilities(facs);
        setNames(new Map(emps.map((e) => [e.id, e.name])));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { void load(); }, []);

  const nameFor = (ref: string) => names.get(ref) || `${ref.slice(0, 8)}…`;

  const pending = useMemo(
    () => applications.filter((a) => a.status === 'eligibility_passed' || a.status === 'risk_review'),
    [applications],
  );
  const stats = useMemo(() => {
    const active = facilities.filter((f) => f.status === 'active');
    return {
      active: active.length,
      pending: pending.length,
      outstanding: active.reduce((s, f) => s + f.outstandingBalance, 0),
      currency: facilities[0]?.currency ?? 'TZS',
    };
  }, [facilities, pending]);

  const setForm = (pid: string, patch: Partial<ProductForm>) =>
    setForms((prev) => ({ ...prev, [pid]: { ...prev[pid], ...patch } }));

  const saveProgram = async (p: CreditProduct) => {
    const f = forms[p.id];
    if (!f) return;
    const rate = f.rate === '' ? null : Number(f.rate);
    if (rate != null && p.maxAnnualRate > 0 && rate > p.maxAnnualRate) {
      toast.error(`Rate can't exceed the platform ceiling of ${p.maxAnnualRate}%.`);
      return;
    }
    setForm(p.id, { saving: true });
    try {
      await creditApi.saveProgram(p.id, {
        enabled: f.enabled,
        approvalWorkflow: f.workflow,
        annualRateOverride: rate,
        maxAmountOverride: f.maxAmount === '' ? null : Number(f.maxAmount),
        maxTermOverride: f.maxTerm === '' ? null : Number(f.maxTerm),
      });
      toast.success(`${p.name} settings saved.`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not save. HR or Finance access is required.'), { duration: 6000 });
    } finally {
      setForm(p.id, { saving: false });
    }
  };

  const approve = async (a: CreditApplication) => {
    setBusyId(a.id);
    try {
      await creditApi.approveApplication(a.id);
      // Move it out of the pending queue; the employee now accepts the agreement.
      setApplications((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: 'approved' } : x)));
      toast.success(`${nameFor(a.employeeRef)}'s loan was approved — awaiting their acceptance.`);
    } catch (e: any) {
      // ⚠️ Approving a loan moves money. If the server did not answer — or
      // answered 5xx, which means it was REACHED and may have committed before
      // falling over — we do not know whether this went through, and
      // "could not approve, try again" invites approving it twice.
      const advice = getSubmissionAdvice(e, { consequential: true });
      toast.error(advice.message, { duration: advice.action === 'check' ? 12000 : 6000 });
      if (advice.action === 'check') {
        // The list is the only honest next step: it shows what actually
        // happened, rather than asking them to guess.
        void load();
      }
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (a: CreditApplication) => {
    const ok = await confirm({
      title: 'Reject loan request?',
      message: `Decline ${nameFor(a.employeeRef)}'s request of ${formatMoney(a.requestedAmount, stats.currency)}? They'll be notified.`,
      confirmLabel: 'Reject',
      danger: true,
    });
    if (!ok) return;
    setBusyId(a.id);
    try {
      await creditApi.rejectApplication(a.id);
      setApplications((prev) => prev.map((x) => (x.id === a.id ? { ...x, status: 'rejected' } : x)));
      toast.success(`${nameFor(a.employeeRef)}'s request was rejected.`);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not reject the request.'), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">Loans &amp; Advances</h1>
        <p className="text-muted-foreground mt-1">
          Configure your lending products, review employee requests, and track active loans.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Coins} label="Active" value={String(stats.active)} tone="info" />
        <StatCard icon={Wallet} label="Pending" value={String(stats.pending)} />
        <StatCard icon={CircleDollarSign} label="Outstanding" value={formatMoney(stats.outstanding, stats.currency)} />
      </div>

      <AgreementCountersignCard />

      <CompanyStampCard />

      <SettlementConfirmationsCard />

      {/* Product config — enable a product + tune the rate within the ceiling */}
      <Card>
        <CardContent className="p-5 space-y-5">
          <div>
            <h2 className="font-semibold text-foreground">Lending products</h2>
            <p className="text-sm text-muted-foreground">
              Enable a product for your company and set your rate (within the platform ceiling) and limits.
            </p>
          </div>
          {loading && <Skeleton className="h-24 w-full" />}
          {!loading && loadError && (
            <DataState variant="error" onRetry={load} message="Couldn't load lending products." />
          )}
          {!loading && !loadError && catalogue.length === 0 && (
            <p className="text-sm text-muted-foreground">No lending products are available from the platform yet.</p>
          )}
          {!loading && !loadError && catalogue.map((p) => {
            const f = forms[p.id];
            if (!f) return null;
            return (
              <div key={p.id} className="rounded-lg border border-border p-4 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium text-foreground">{p.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {p.interestMethod === 'reducing' ? 'Reducing balance' : 'Flat'} · default {p.defaultAnnualRate}%
                      {p.maxAnnualRate > 0 ? ` · ceiling ${p.maxAnnualRate}%` : ''}
                    </span>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={f.enabled} onChange={(e) => setForm(p.id, { enabled: e.target.checked })} />
                    Enabled
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      Rate (%){p.maxAnnualRate > 0 ? ` · max ${p.maxAnnualRate}` : ''}
                    </label>
                    <input type="number" min={0} step="0.1" max={p.maxAnnualRate > 0 ? p.maxAnnualRate : undefined}
                      value={f.rate} placeholder={`${p.defaultAnnualRate} (default)`}
                      onChange={(e) => setForm(p.id, { rate: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Max amount</label>
                    <input type="number" min={0} value={f.maxAmount} placeholder={p.maxAmount ? String(p.maxAmount) : 'none'}
                      onChange={(e) => setForm(p.id, { maxAmount: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Max term (months)</label>
                    <input type="number" min={0} value={f.maxTerm} placeholder={p.maxTermMonths ? String(p.maxTermMonths) : 'none'}
                      onChange={(e) => setForm(p.id, { maxTerm: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">Approval</label>
                    <select value={f.workflow} onChange={(e) => setForm(p.id, { workflow: e.target.value })} className={input}>
                      <option value="single">Single approver</option>
                      <option value="dual">Dual control</option>
                      <option value="chain">Approval chain</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => saveProgram(p)} disabled={f.saving}>{f.saving ? 'Saving…' : 'Save'}</Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Approval queue */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border px-5 py-4">
            <h2 className="font-semibold text-foreground">Requests to review</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center">Term</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 2 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                ))}
              {!loading && pending.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No requests waiting.</TableCell>
                </TableRow>
              )}
              {!loading && pending.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium text-charcoal">{nameFor(a.employeeRef)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(a.requestedAmount, stats.currency)}</TableCell>
                  <TableCell className="text-center tabular-nums text-muted-foreground">{a.requestedTermMonths} mo</TableCell>
                  <TableCell><Badge variant={applicationPill(a.status).variant}>{applicationPill(a.status).label}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button size="sm" variant="outline" disabled={busyId === a.id} onClick={() => approve(a)}>
                        {busyId === a.id ? 'Approving…' : 'Approve'}
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busyId === a.id}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => reject(a)}>
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Active facilities overview */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border px-5 py-4">
            <h2 className="font-semibold text-foreground">Active loans</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Principal</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Installment</TableHead>
                <TableHead className="text-center">Term</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 2 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                ))}
              {!loading && facilities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No active loans.</TableCell>
                </TableRow>
              )}
              {!loading && facilities.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="font-medium text-charcoal">{nameFor(f.employeeRef)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(f.principal, f.currency)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums text-charcoal">{formatMoney(f.outstandingBalance, f.currency)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(f.installment, f.currency)}</TableCell>
                  <TableCell className="text-center tabular-nums text-muted-foreground">{f.termMonths} mo</TableCell>
                  <TableCell><Badge variant={facilityPill(f.status).variant}>{facilityPill(f.status).label}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoansPage;

// Partner settlement confirmations — the employer's half of the settlement
// handshake: the partner's statement says your payroll collected X for them
// this period; confirm it (or dispute with a note). Employees see nothing
// here — their record is the payslip.
function SettlementConfirmationsCard() {
  const promptText = usePrompt();
  const [items, setItems] = useState<SettlementConfirmation[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [lines, setLines] = useState<Record<string, { employee_name: string; product_name: string; collected: number }[]>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => creditApi.settlementConfirmations().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const pending = items.filter((c) => c.status === 'pending');
  if (items.length === 0) return null;

  const toggle = async (id: string) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next && !lines[next]) {
      const l = await creditApi.settlementConfirmationLines(next).catch(() => []);
      setLines((m) => ({ ...m, [next]: l }));
    }
  };

  const decide = async (id: string, decision: 'confirm' | 'dispute') => {
    let note: string | undefined;
    if (decision === 'dispute') {
      note = (await promptText({
        title: 'Dispute settlement',
        label: 'What disagrees with your records?',
        confirmLabel: 'Record dispute',
        danger: true,
        required: true,
      })) ?? '';
      if (!note.trim()) return;
    }
    setBusyId(id);
    try {
      await creditApi.decideSettlementConfirmation(id, decision, note);
      toast.success(decision === 'confirm' ? 'Settlement confirmed.' : 'Dispute recorded — the partner cannot mark this paid until it is resolved.');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not record your decision.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-foreground">Partner settlement confirmations</h2>
            <p className="text-sm text-muted-foreground">
              Lending partners' period statements of what your payroll collected for them. Confirm each, or dispute with a note.
            </p>
          </div>
          {pending.length > 0 && <Badge variant="secondary">{pending.length} pending</Badge>}
        </div>
        <div className="mt-4 divide-y divide-border">
          {items.slice(0, 8).map((c) => (
            <div key={c.id}>
              <button type="button" onClick={() => toggle(c.id)} className="flex w-full flex-wrap items-center gap-3 py-3 text-left">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {c.partner_name || 'Partner'} · {formatDate(c.period_start)} – {formatDate(c.period_end)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(c.collected).toLocaleString()} collected across {c.employees} employee(s)
                  </p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  c.status === 'confirmed' ? 'bg-success-soft text-success-strong'
                  : c.status === 'disputed' ? 'bg-danger-soft text-danger-strong'
                  : 'bg-warning-soft text-warning-strong'}`}>
                  {c.status}
                </span>
                {c.status === 'pending' && (
                  <span className="flex gap-2">
                    <Button size="sm" disabled={busyId === c.id}
                      onClick={(e) => { e.stopPropagation(); decide(c.id, 'confirm'); }}>
                      Confirm
                    </Button>
                    <Button size="sm" variant="outline" disabled={busyId === c.id}
                      onClick={(e) => { e.stopPropagation(); decide(c.id, 'dispute'); }}>
                      Dispute
                    </Button>
                  </span>
                )}
                {c.status === 'disputed' && (
                  <Button size="sm" variant="outline" disabled={busyId === c.id}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setBusyId(c.id);
                      try {
                        await creditApi.reopenSettlementConfirmation(c.id);
                        toast.success('Dispute withdrawn — you can now confirm the settlement.');
                        await load();
                      } catch (err) {
                        toast.error(getApiErrorMessage(err, 'Could not withdraw the dispute.'));
                      } finally {
                        setBusyId(null);
                      }
                    }}>
                    Withdraw dispute
                  </Button>
                )}
              </button>
              {openId === c.id && (
                <div className="mb-3 rounded-lg bg-muted/40 px-4 py-2">
                  {(lines[c.id] ?? []).length === 0 ? (
                    <p className="py-1 text-xs text-muted-foreground">Loading…</p>
                  ) : (
                    (lines[c.id] ?? []).map((l, i) => (
                      <div key={i} className="flex items-center justify-between py-1 text-sm">
                        <span className="text-foreground">{l.employee_name || 'Employee'} <span className="text-muted-foreground">· {l.product_name}</span></span>
                        <span className="tabular-nums font-medium text-foreground">{Math.round(l.collected).toLocaleString()}</span>
                      </div>
                    ))
                  )}
                  {c.note && <p className="mt-1 text-xs text-danger">Dispute note: {c.note}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Loan agreements awaiting the employer's countersign — the org's seat in the
// employee → employer → partner signing chain. Signing here acknowledges the
// payroll-deduction arrangement; it does not gate disbursement.
function AgreementCountersignCard() {
  const promptText = usePrompt();
  const [items, setItems] = useState<{ agreement_id: string; borrower_name: string; product_name: string; principal: number; currency: string; requested_at: string }[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [signingId, setSigningId] = useState<string | null>(null);

  const load = () => creditApi.countersignPending().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (items.length === 0) return null;

  const sign = async (id: string, mark: { signature_png?: string; typed_name?: string }) => {
    setBusyId(id);
    try {
      await creditApi.countersignAgreement(id, mark);
      toast.success('Agreement countersigned.');
      setSigningId(null);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not countersign.'), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  const decline = async (id: string) => {
    const note = await promptText({
      title: 'Decline signing',
      message: 'The reason is recorded on the signing flow and a corrected agreement will need a new one.',
      label: 'Why are you declining?',
      confirmLabel: 'Decline',
      danger: true,
      required: true,
    });
    if (!note || !note.trim()) return;
    setBusyId(id);
    try {
      await creditApi.declineAgreementSigning(id, note.trim());
      toast.success('Signing declined.');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not decline.'), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-foreground">
              Agreements awaiting your countersign
              <Badge variant="warning">{items.length}</Badge>
            </h2>
            <p className="text-sm text-muted-foreground">
              The employee has signed their loan agreement; your countersign acknowledges the payroll-deduction arrangement before the funding partner completes the chain.
            </p>
          </div>
        </div>
        {signingId && (
          <CountersignModal
            busy={busyId === signingId}
            onClose={() => setSigningId(null)}
            onSign={(mark) => sign(signingId, mark)}
          />
        )}
        <div className="mt-3 divide-y divide-border">
          {items.map((it) => (
            <div key={it.agreement_id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{it.borrower_name || 'Employee'} — {it.product_name}</p>
                <p className="text-xs text-muted-foreground">{formatMoney(it.principal, it.currency as Currency)} · requested {formatDate(it.requested_at)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button size="sm" disabled={busyId === it.agreement_id} onClick={() => setSigningId(it.agreement_id)}>
                  Countersign
                </Button>
                <Button size="sm" variant="outline" disabled={busyId === it.agreement_id} onClick={() => decline(it.agreement_id)}>
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Employer countersign — draw + type on behalf of the company; the record
// names the person who signed for the org.
function CountersignModal({ busy, onClose, onSign }: {
  busy: boolean;
  onClose: () => void;
  onSign: (mark: { signature_png?: string; typed_name?: string }) => void;
}) {
  const padRef = React.useRef<SignaturePadHandle | null>(null);
  const [typedName, setTypedName] = useState('');
  const [hasInk, setHasInk] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 font-semibold text-foreground">Countersign for your company</h3>
        <p className="mb-4 text-xs text-muted-foreground">
          Your signature acknowledges the payroll-deduction arrangement. The record names you as the person signing on the company&apos;s behalf.
        </p>
        <div className="space-y-4">
          <SignaturePad padRef={padRef} onChange={(empty) => setHasInk(!empty)} />
          <input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="Type your full legal name"
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!hasInk || typedName.trim().length < 3 || busy}
            onClick={() => onSign({ signature_png: padRef.current?.toPNG() ?? undefined, typed_name: typedName.trim() })}>
            {busy ? 'Signing…' : 'Sign and Complete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Company stamp — uploaded by one admin, approved by a second (dual control),
// then applied automatically to loan agreements when the employer countersigns.
// The stamp image is rendered server-side into the final signed PDF and is
// never downloadable from here.
function CompanyStampCard() {
  const [stamps, setStamps] = useState<{ id: string; name: string; status: string; created_by: string }[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = () => creditApi.listStamps().then(setStamps).catch(() => setStamps([]));
  useEffect(() => { load(); }, []);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png') { toast.error('The stamp must be a PNG (transparent background recommended).'); return; }
    if (file.size > 256 * 1024) { toast.error('The stamp must be under 256KB.'); return; }
    setUploading(true);
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(String(rd.result).split(',')[1] ?? '');
        rd.onerror = reject;
        rd.readAsDataURL(file);
      });
      await creditApi.registerStamp(file.name.replace(/\.png$/i, ''), b64);
      toast.success('Stamp uploaded — a different administrator must approve it before use.');
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not upload the stamp.'), { duration: 6000 });
    } finally {
      setUploading(false);
    }
  };

  const act = async (id: string, fn: () => Promise<void>, ok: string) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(ok);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Action failed.'), { duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-foreground">Company stamp</h2>
            <p className="text-sm text-muted-foreground">
              Applied automatically when you countersign loan agreements and shown in the final signed PDF.
              Upload by one admin, approval by a second.
            </p>
          </div>
          <label className="cursor-pointer">
            <span className="inline-flex h-9 items-center rounded-md bg-primary-500 px-3 text-sm font-medium text-white hover:bg-primary-500/90">
              {uploading ? 'Uploading…' : 'Upload stamp (PNG)'}
            </span>
            <input type="file" accept="image/png" className="hidden" onChange={onFile} disabled={uploading} />
          </label>
        </div>
        {stamps.length > 0 && (
          <div className="mt-3 divide-y divide-border">
            {stamps.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{s.name || 'Official stamp'}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.status === 'pending_approval' ? 'Awaiting approval by a second administrator'
                      : s.status === 'active' ? 'Active — applies on countersign' : 'Revoked'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {s.status === 'pending_approval' && (
                    <Button size="sm" disabled={busyId === s.id}
                      onClick={() => act(s.id, () => creditApi.approveStamp(s.id), 'Stamp approved and active.')}>
                      Approve
                    </Button>
                  )}
                  {s.status !== 'revoked' && (
                    <Button size="sm" variant="outline" disabled={busyId === s.id}
                      onClick={() => act(s.id, () => creditApi.revokeStamp(s.id), 'Stamp revoked.')}>
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
